import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { IssueCounts } from '@/types/issue';
import {
  Alert,
  Box,
  Card,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
} from '@mui/x-data-grid';

import { useIssues } from '@/hooks/useIssues';
import { useAnalysisEvents } from '@/hooks/useAnalysisEvents';
import type { AnalysisSSEEvent } from '@/hooks/useAnalysisEvents';
import { issues as issuesApi } from '@/helpers/ApiHelper';
import type { Issue, AnalysisSummary, AnalysisState } from '@/types/issue';
import type { Severity } from '@/types/analysis';
import { SeverityBadge } from '@/components/SeverityBadge';
import { ServiceHealthCards } from '@/components/ServiceHealthCards';
import { LogEventTable } from '@/components/LogEventTable';
import { IssueRowActions } from '@/components/IssueRowActions';
import { ProjectFilter } from '@/components/ProjectFilter';
import { useProjects } from '@/hooks/useProjects';
import type { Project } from '@/types/project';
import { formatRelative } from '@/utils/formatRelative';
import { levelChipColor } from '@/utils/levelChipColor';

// ---------------------------------------------------------------------------
// Override map — single source of truth for local analysis state
//
// overrides: Map<issueId, AnalysisSummary | null | "pending">
//
//   "pending"          → user clicked Analyze; awaiting server confirmation
//   null               → optimistic cancel (awaiting SSE analysis_cancelled)
//   AnalysisSummary    → SSE settled the real analysis object
//   (entry absent)     → use server row's latest_analysis as-is
// ---------------------------------------------------------------------------

type OverrideValue = AnalysisSummary | null | 'pending';

function getEffectiveSummary(
  row: Issue,
  overrides: Map<string, OverrideValue>,
): AnalysisSummary | null {
  const override = overrides.get(row.id);
  if (override === undefined) return row.latest_analysis;
  if (override === 'pending') return null; // pending = no analysis shown yet
  return override;
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

function buildColumns(
  overrides: Map<string, OverrideValue>,
  onOptimisticUpdate: (issueId: string, patch: Partial<AnalysisSummary> | 'pending' | null) => void,
  onRevert: (issueId: string) => void,
  /** Sentry project slug -> registry row, for the Project column's display name. */
  projectsBySlug: Map<string, Project>,
): GridColDef[] {
  return [
    {
      field: 'title',
      headerName: 'Title',
      flex: 2,
      minWidth: 280,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as Issue;
        return (
          <Box
            component={Link}
            to={`/issues/${row.id}`}
            state={{ issue: row }}
            sx={{
              color: 'text.primary',
              textDecoration: 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              width: '100%',
              '&:hover': { textDecoration: 'underline', color: 'primary.main' },
            }}
          >
            {params.value as string}
          </Box>
        );
      },
    },
    {
      field: 'project',
      headerName: 'Project',
      width: 160,
      renderCell: (params: GridRenderCellParams) => {
        // The row carries the raw Sentry project slug. Prefer the registry's
        // product name, then its canonical key, and fall back to the slug when
        // the slug isn't mapped to any project. The slug is kept as a second
        // line whenever it differs, since it's what Sentry itself shows.
        const slug = (params.value as string) ?? '';
        const project = projectsBySlug.get(slug);
        const name = project?.product ?? project?.project_key ?? slug;
        return (
          <Box sx={{ minWidth: 0, py: 0.5 }}>
            <Typography variant="body2" noWrap>
              {name}
            </Typography>
            {name !== slug && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {slug}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'level',
      headerName: 'Level',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          size="small"
          label={params.value as string}
          color={levelChipColor((params.value as string) ?? '')}
        />
      ),
    },
    {
      field: 'latest_analysis',
      headerName: 'Severity',
      width: 160,
      sortable: true,
      // DataGrid v7: valueGetter(value, row, col, apiRef)
      valueGetter: (_value: unknown, row: unknown) => {
        const issueRow = row as Issue;
        const eff = getEffectiveSummary(issueRow, overrides);
        return eff?.severity ?? null;
      },
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as Issue;
        const eff = getEffectiveSummary(row, overrides);
        const sev = eff?.severity as Severity | null | undefined;
        if (!sev) {
          return <Typography variant="body2" color="text.disabled">—</Typography>;
        }
        return <SeverityBadge severity={sev} size="small" />;
      },
    },
    {
      field: 'last_seen',
      headerName: 'Last seen',
      width: 150,
      renderCell: (params: GridRenderCellParams) =>
        (params.value as string) ? formatRelative(params.value as string) : '—',
    },
    {
      field: 'count',
      headerName: 'Events',
      type: 'number',
      width: 80,
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as Issue;
        const eff = getEffectiveSummary(row, overrides);
        return (
          <IssueRowActions
            issueId={row.id}
            latestAnalysis={eff}
            onOptimisticUpdate={onOptimisticUpdate}
            onRevert={onRevert}
          />
        );
      },
    },
  ];
}

// ---------------------------------------------------------------------------
// Filter constants
// ---------------------------------------------------------------------------

const STATUS_CHIPS: { value: string; label: string }[] = [
  { value: 'unresolved', label: 'unresolved' },
  { value: 'resolved', label: 'resolved' },
  { value: 'ignored', label: 'ignored' },
];

const LEVEL_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'fatal', label: 'Fatal' },
  { value: 'error', label: 'Error' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
  { value: 'debug', label: 'Debug' },
];

// Analysis state segments
interface AnalysisSegment {
  value: AnalysisState | 'all';
  label: string;
}

const ANALYSIS_SEGMENTS: AnalysisSegment[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'analyzed', label: 'Analyzed' },
];

// ---------------------------------------------------------------------------
// Tail-f pulse indicator
// ---------------------------------------------------------------------------

interface TailFIndicatorProps {
  hasRecent: boolean;
  onRefresh: () => void;
}

function TailFIndicator({ hasRecent, onRefresh }: TailFIndicatorProps) {
  const theme = useTheme();
  return (
    <Tooltip title={hasRecent ? 'New issues in the last 5 min — click to refresh' : 'Watching for new issues (polls every 30s)'}>
      <Box
        role="button"
        tabIndex={0}
        aria-label={hasRecent ? 'New issues detected — click to refresh' : 'tail -f: watching for new issues'}
        onClick={onRefresh}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onRefresh();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          cursor: 'pointer',
          userSelect: 'none',
          minHeight: 44,
          minWidth: 44,
          px: 1,
          borderRadius: 1,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
          '&:focus-visible': {
            outline: `1px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            flexShrink: 0,
            ...(hasRecent
              ? {
                  animation: 'tailf-pulse 1.4s ease-in-out infinite',
                  '@keyframes tailf-pulse': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.4, transform: 'scale(1.4)' },
                  },
                }
              : { opacity: 0.5 }),
          }}
        />
        <Typography
          component="span"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 11,
            color: 'primary.main',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          tail -f
        </Typography>
      </Box>
    </Tooltip>
  );
}

// Numeric weight for severity sorting
const SEVERITY_WEIGHT: Record<Severity, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

type ActiveTab = 'issues' | 'errors';

const TAILF_POLL_INTERVAL_MS = 30_000;
const RECENT_THRESHOLD_MS = 5 * 60 * 1_000;

// ---------------------------------------------------------------------------
// Dispatch buddy-context for analysis events
// ---------------------------------------------------------------------------

function dispatchBuddyContext(
  status: 'running' | 'done' | 'failed' | 'cancelled',
  identifier?: string,
  // `job` enables DetectiveFooter's polling reconciler to recover the
  // terminal state if the user navigates away from this list before
  // the SSE event for the analysis finishing arrives. analyses.get()
  // is keyed on issueId, so type='analysis' / id=issueId.
  job?: { type: 'analysis' | 'investigation'; id: string },
): void {
  window.dispatchEvent(
    new CustomEvent('sherlock:buddy-context', {
      detail: { kind: 'job', status, identifier, job },
    }),
  );
}

export function IssueListPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tab, setTab] = useState<ActiveTab>('issues');
  const [statusFilter, setStatusFilter] = useState('unresolved');
  const [levelFilter, setLevelFilter] = useState('');
  // Registry project_key, '' = all projects. Resolved server-side to that
  // project's Sentry project slugs.
  const [projectFilter, setProjectFilter] = useState('');

  // Reverse index for the Project column: a registry row may map several Sentry
  // slugs, so flatten them all to their owning project.
  const { projects: registryProjects } = useProjects();
  const projectsBySlug = useMemo(() => {
    const map = new Map<string, Project>();
    for (const p of registryProjects) {
      for (const slug of p.sentry_project_slugs) {
        if (!map.has(slug)) map.set(slug, p);
      }
    }
    return map;
  }, [registryProjects]);
  const [analysisSegment, setAnalysisSegment] = useState<AnalysisState | 'all'>('all');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const [sortBySeverity, setSortBySeverity] = useState(false);
  const [hasTailFRecent, setHasTailFRecent] = useState(false);

  // Single source of truth for local overrides.
  // Replaces the former optimisticMap + ssePatches two-map architecture.
  const [overrides, setOverrides] = useState<Map<string, OverrideValue>>(new Map());

  // Server-side segment counts from /api/issues/counts — does NOT change with
  // analysis_state filter, so it always reflects the full totals.
  const [counts, setCounts] = useState<IssueCounts | null>(null);
  // Stable ref so SSE handler can trigger a refetch without re-subscribing.
  const countsAbortRef = useRef<AbortController | null>(null);

  // ── Fetch segment counts from /api/issues/counts ──────────────────────
  // Called on mount, on status/level changes, and on terminal SSE events.

  const fetchCounts = useCallback(() => {
    // Abort any in-flight request before starting a new one.
    countsAbortRef.current?.abort();
    const controller = new AbortController();
    countsAbortRef.current = controller;

    void issuesApi
      .counts({
        status: statusFilter || undefined,
        level: levelFilter || undefined,
        project: projectFilter || undefined,
      })
      .then((c) => {
        if (!controller.signal.aborted) setCounts(c);
      })
      .catch(() => {
        // Non-fatal: counts stays null or stale.
      });
  }, [statusFilter, levelFilter, projectFilter]);

  useEffect(() => {
    fetchCounts();
    return () => {
      countsAbortRef.current?.abort();
    };
  }, [fetchCounts]);

  const { data, isLoading, error, refetch } = useIssues({
    status: statusFilter || undefined,
    level: levelFilter || undefined,
    analysis_state: analysisSegment !== 'all' ? analysisSegment : undefined,
    project: projectFilter || undefined,
    page: paginationModel.page + 1,
  });

  // ── Evict stale override entries when the page changes ──────────────────
  // The override map only grows as users interact with rows; prune on page
  // change so entries from pages we've left don't accumulate indefinitely.
  // We key eviction on the resolved page of results so it doesn't fire on
  // the same page twice.
  const prevPageRef = useRef<number>(paginationModel.page);
  useEffect(() => {
    if (paginationModel.page === prevPageRef.current) return;
    prevPageRef.current = paginationModel.page;
    setOverrides(new Map()); // clean slate on page navigation
  }, [paginationModel.page]);

  // ── SSE event handler ───────────────────────────────────────────────────

  const handleAnalysisEvent = useCallback((event: AnalysisSSEEvent) => {
    if (event.event === 'heartbeat') return;

    if (event.event === 'analysis_started') {
      const d = event.data;
      // Replace the "pending" optimistic marker with the real (running) summary
      // that came back from the server.
      setOverrides((prev) => {
        const next = new Map(prev);
        next.set(d.issue_id, {
          id: d.analysis_id,
          status: 'running',
          severity: null,
          completed_at: null,
          cancelled_at: null,
          cost_usd: null,
        });
        return next;
      });
      // Counts: issue moved from pending → in_progress
      fetchCounts();
      dispatchBuddyContext('running', d.issue_id.slice(0, 8), {
        type: 'analysis',
        id: d.issue_id,
      });
    }

    if (event.event === 'analysis_progress') {
      // Row is already showing "running" from analysis_started.
      // No additional patch needed; SSE will settle on completed/failed.
    }

    if (event.event === 'analysis_completed') {
      const d = event.data;
      setOverrides((prev) => {
        const next = new Map(prev);
        next.set(d.issue_id, {
          id: d.analysis_id,
          status: 'completed',
          severity: d.severity,
          completed_at: d.completed_at,
          cancelled_at: null,
          cost_usd: d.cost_usd,
        });
        return next;
      });
      // Counts: completed analysis shifts in_progress → analyzed
      fetchCounts();
      dispatchBuddyContext('done', d.issue_id.slice(0, 8), {
        type: 'analysis',
        id: d.issue_id,
      });
    }

    if (event.event === 'analysis_failed') {
      const d = event.data;
      setOverrides((prev) => {
        const next = new Map(prev);
        next.set(d.issue_id, {
          id: d.analysis_id,
          status: 'failed',
          severity: null,
          completed_at: null,
          cancelled_at: null,
          cost_usd: null,
        });
        return next;
      });
      // Counts: failed analysis returns issue to pending
      fetchCounts();
      dispatchBuddyContext('failed', d.issue_id.slice(0, 8), {
        type: 'analysis',
        id: d.issue_id,
      });
    }

    if (event.event === 'analysis_cancelled') {
      const d = event.data;
      setOverrides((prev) => {
        const next = new Map(prev);
        next.set(d.issue_id, {
          id: d.analysis_id,
          status: 'failed',
          severity: null,
          completed_at: null,
          cancelled_at: d.cancelled_at,
          cost_usd: null,
        });
        return next;
      });
      // Counts: cancelled analysis returns issue to pending
      fetchCounts();
      dispatchBuddyContext('cancelled', d.issue_id.slice(0, 8), {
        type: 'analysis',
        id: d.issue_id,
      });
    }
  }, [fetchCounts]);

  useAnalysisEvents({ onEvent: handleAnalysisEvent });

  // ── Optimistic update helpers ────────────────────────────────────────────

  const handleOptimisticUpdate = useCallback(
    (issueId: string, patch: Partial<AnalysisSummary> | 'pending' | null) => {
      setOverrides((prev) => {
        const next = new Map(prev);
        if (patch === 'pending') {
          next.set(issueId, 'pending');
        } else {
          // null = optimistic cancel (hide analysis until SSE settles it)
          next.set(issueId, patch as AnalysisSummary | null);
        }
        return next;
      });
    },
    [],
  );

  const handleRevert = useCallback((issueId: string) => {
    // Delete the entry so the server row wins cleanly (no revert sentinel needed)
    setOverrides((prev) => {
      const next = new Map(prev);
      next.delete(issueId);
      return next;
    });
  }, []);

  // ── Tail-f: derive hasTailFRecent ──────────────────────────────────────

  useEffect(() => {
    if (statusFilter !== 'unresolved') { setHasTailFRecent(false); return; }
    if (!data?.results.length) { setHasTailFRecent(false); return; }
    const now = Date.now();
    setHasTailFRecent(
      data.results.some(
        (issue) => now - new Date(issue.last_seen).getTime() < RECENT_THRESHOLD_MS,
      ),
    );
  }, [data, statusFilter]);

  // ── Tail-f: 30s polling ────────────────────────────────────────────────

  useEffect(() => {
    if (statusFilter !== 'unresolved') return;
    const id = setInterval(() => { void refetch(); }, TAILF_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [statusFilter, refetch]);

  // ── Build rows with overrides applied ──────────────────────────────────

  const rows: Issue[] = data?.results ?? [];

  // ── Segment counts from /api/issues/counts (server-side, all pages) ────
  // Bug fix: "All" uses counts?.all directly — no data?.count fallback.
  // data?.count is the filtered count (reflects the active analysis_state
  // filter), so the fallback would show wrong numbers when a segment is active.
  // While counts are loading, "All" shows no number — consistent with the
  // other segments.

  const segmentCounts = {
    all: counts?.all,
    pending: counts?.pending,
    in_progress: counts?.in_progress,
    analyzed: counts?.analyzed,
  } as const;

  // Client-side severity sort
  const sortedRows: Issue[] = sortBySeverity
    ? [...rows].sort((a, b) => {
        const sa = getEffectiveSummary(a, overrides)?.severity as Severity | null;
        const sb = getEffectiveSummary(b, overrides)?.severity as Severity | null;
        const wa = sa ? SEVERITY_WEIGHT[sa] : 0;
        const wb = sb ? SEVERITY_WEIGHT[sb] : 0;
        return wb - wa;
      })
    : rows;

  const columns = buildColumns(
    overrides,
    handleOptimisticUpdate,
    handleRevert,
    projectsBySlug,
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Page title row */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h4">Issues</Typography>
        {statusFilter === 'unresolved' && (
          <TailFIndicator
            hasRecent={hasTailFRecent}
            onRefresh={() => { void refetch(); }}
          />
        )}
      </Stack>

      {/* Service health cards */}
      <Box sx={{ mb: 3 }}>
        <ServiceHealthCards />
      </Box>

      {/* Tabbed view */}
      <Tabs
        value={tab}
        onChange={(_, v: ActiveTab) => setTab(v)}
        aria-label="Issue source"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Issues" value="issues" />
        <Tab label="Recent errors" value="errors" />
      </Tabs>

      {/* Issues tab */}
      {tab === 'issues' && (
        <Box role="tabpanel" aria-label="Issues panel">

          {/* ── Analysis state segmented control ── */}
          <Box sx={{ mb: 2.5 }}>
            <ToggleButtonGroup
              value={analysisSegment}
              exclusive
              onChange={(_, newVal: AnalysisState | 'all' | null) => {
                if (newVal !== null) {
                  setAnalysisSegment(newVal);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }
              }}
              aria-label="Filter issues by analysis state"
              size="small"
              sx={{
                gap: 0.5,
                '& .MuiToggleButtonGroup-grouped': {
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '3px !important',
                  mx: 0,
                  '&:not(:last-of-type)': { borderRadius: '3px !important' },
                  '&:not(:first-of-type)': {
                    borderRadius: '3px !important',
                    borderLeft: '1px solid',
                    borderLeftColor: 'divider',
                    marginLeft: '4px',
                  },
                },
              }}
            >
              {ANALYSIS_SEGMENTS.map((seg) => {
                const count = segmentCounts[seg.value];
                const isActive = analysisSegment === seg.value;
                return (
                  <ToggleButton
                    key={seg.value}
                    value={seg.value}
                    aria-label={
                      count !== undefined
                        ? `${seg.label} — ${count} issues`
                        : seg.label
                    }
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      px: 1.5,
                      py: 0.625,
                      minHeight: 32,
                      transition: theme.transitions.create(
                        ['background-color', 'color', 'border-color', 'box-shadow'],
                        { duration: theme.transitions.duration.shorter },
                      ),
                      ...(isActive
                        ? {
                            bgcolor: 'rgba(217, 119, 87, 0.12)',
                            color: 'primary.main',
                            borderColor: 'primary.main',
                            boxShadow: '0 0 0 1px rgba(217, 119, 87, 0.30)',
                            '&:hover': { bgcolor: 'rgba(217, 119, 87, 0.18)' },
                          }
                        : {
                            bgcolor: 'transparent',
                            color: 'text.secondary',
                            '&:hover': {
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              bgcolor: 'rgba(217, 119, 87, 0.06)',
                            },
                          }),
                      '&.Mui-selected': {
                        bgcolor: 'rgba(217, 119, 87, 0.12)',
                        color: 'primary.main',
                      },
                      '&.Mui-selected:hover': { bgcolor: 'rgba(217, 119, 87, 0.18)' },
                    }}
                  >
                    {seg.label}
                    {count !== undefined && (
                      <Typography
                        component="span"
                        sx={{
                          ml: 0.75,
                          fontSize: 10,
                          fontFamily: '"JetBrains Mono", monospace',
                          color: isActive ? 'primary.main' : 'text.disabled',
                          opacity: 0.85,
                        }}
                      >
                        · {count}
                      </Typography>
                    )}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Box>

          {/* ── Status filter chips ── */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 2 }}
            flexWrap="wrap"
            useFlexGap
            role="radiogroup"
            aria-label="Filter issues by status"
          >
            {STATUS_CHIPS.map((chip) => {
              const active = statusFilter === chip.value;
              return (
                <Chip
                  key={chip.value}
                  label={chip.label}
                  role="radio"
                  aria-checked={active}
                  clickable
                  onClick={() => {
                    setStatusFilter(chip.value);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                  variant={active ? 'filled' : 'outlined'}
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    minHeight: 32,
                    ...(active
                      ? {
                          borderColor: 'primary.main',
                          bgcolor: 'rgba(217, 119, 87, 0.10)',
                          color: 'primary.main',
                          '&:hover': { bgcolor: 'rgba(217, 119, 87, 0.16)' },
                          '& .MuiChip-label': { color: 'primary.main' },
                        }
                      : {
                          borderColor: 'divider',
                          color: 'text.secondary',
                          '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                        }),
                  }}
                />
              );
            })}
          </Stack>

          {/* ── Level filter + sort row ── */}
          <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center" flexWrap="wrap" useFlexGap>
            <Stack
              direction="row"
              spacing={0.75}
              flexWrap="wrap"
              useFlexGap
              role="radiogroup"
              aria-label="Filter issues by level"
            >
              {LEVEL_OPTIONS.map((opt) => {
                const active = levelFilter === opt.value;
                return (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    role="radio"
                    aria-checked={active}
                    clickable
                    onClick={() => {
                      setLevelFilter(opt.value);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                    variant={active ? 'filled' : 'outlined'}
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: 10,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      minHeight: 28,
                      ...(active
                        ? {
                            borderColor: theme.palette.primary.main,
                            bgcolor: 'rgba(217, 119, 87, 0.10)',
                            color: 'primary.main',
                            '& .MuiChip-label': { color: 'primary.main' },
                          }
                        : {
                            borderColor: 'divider',
                            color: 'text.secondary',
                          }),
                    }}
                  />
                );
              })}
            </Stack>

            <ProjectFilter
              value={projectFilter}
              onChange={(key) => {
                setProjectFilter(key);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              requires="sentry"
              allLabel="All projects"
              // Match the surrounding filter row's mono treatment. Kept at the
              // call site, not in the component: /investigate's neighbours are
              // plain TextFields, where mono would be the odd one out. Project
              // keys stay lowercase — they're identifiers, not labels.
              sx={{
                '& .MuiInputBase-input, & .MuiInputLabel-root': {
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 12,
                },
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={sortBySeverity}
                  onChange={(e) => setSortBySeverity(e.target.checked)}
                  inputProps={{ 'aria-label': 'Sort by severity descending' }}
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{ fontFamily: '"JetBrains Mono", monospace', textTransform: 'uppercase', letterSpacing: '0.03em' }}
                >
                  Sort by severity
                </Typography>
              }
            />
          </Stack>

          {/* Error state */}
          {error && (
            <Alert severity="error" role="alert" sx={{ mb: 3 }}>
              Failed to load issues: {error.message}
            </Alert>
          )}

          {/* Empty state */}
          {!isLoading && !error && data && data.count === 0 && (
            <Alert severity="info">No issues found.</Alert>
          )}

          {/* Data grid (desktop) / card list (mobile) */}
          {(!data || data.count > 0 || isLoading) && !error && (
            <>
              {isMobile ? (
                <Stack spacing={1}>
                  {sortedRows.map((issue) => {
                    const eff = getEffectiveSummary(issue, overrides);
                    return (
                      <Card
                        key={issue.id}
                        component={Link}
                        to={`/issues/${issue.id}`}
                        state={{ issue }}
                        sx={{
                          p: 2,
                          textDecoration: 'none',
                          display: 'block',
                          '&:hover': { borderColor: 'primary.main' },
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={1}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body1"
                              sx={{
                                color: 'text.primary',
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {issue.title}
                            </Typography>
                            <Typography variant="caption" color="text.faintest">
                              {issue.project} · last seen {formatRelative(issue.last_seen)}
                            </Typography>
                          </Box>
                          {eff?.severity && (
                            <SeverityBadge severity={eff.severity} size="small" />
                          )}
                        </Stack>
                      </Card>
                    );
                  })}
                </Stack>
              ) : (
                <DataGrid
                  rows={sortedRows}
                  columns={columns}
                  loading={isLoading}
                  density="compact"
                  disableRowSelectionOnClick
                  pagination
                  paginationMode="server"
                  sortingMode="server"
                  rowCount={data?.count ?? 0}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 50, page: 0 } },
                  }}
                  autoHeight
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                      borderColor: 'divider',
                      fontFamily: '"Inter", sans-serif',
                      fontSize: 13,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'background.deeper',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.faintest',
                    },
                    '& .MuiDataGrid-row': {
                      borderLeft: '2px solid transparent',
                      '&:nth-of-type(odd)': { backgroundColor: 'background.paper' },
                      '&:nth-of-type(even)': { backgroundColor: 'background.elev2' },
                      '&:hover': {
                        borderLeftColor: 'primary.main',
                        backgroundColor: 'primary.soft',
                      },
                    },
                    '& .MuiDataGrid-cell[data-field="last_seen"], & .MuiDataGrid-cell[data-field="count"]':
                      {
                        fontFamily: '"JetBrains Mono", monospace',
                        fontVariantNumeric: 'tabular-nums',
                      },
                    '& .MuiDataGrid-footerContainer': {
                      backgroundColor: 'background.deeper',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                />
              )}
            </>
          )}
        </Box>
      )}

      {/* Recent errors tab */}
      {tab === 'errors' && (
        <Box role="tabpanel" aria-label="Recent errors panel">
          <LogEventTable />
        </Box>
      )}
    </Box>
  );
}
